import React, { useState, useCallback, useEffect, useRef } from 'react'
import VUMeter from './vumeter'
import VCRButton from './VCRButton'
import ColorCycleButton from './ColorCycleButton'
import InputDeviceDropdown from './InputDeviceDropdown'
import PeakLevelMeter from './PeakLevelMeter'
import LedTimeCounter from './Counter'
import FlatButton from './FlatButton'
import LargeFlatButton from './LargeFlatButton'
import RetroKnob from './RetroKnob'
import { useAudioRecorder, RecordFormat } from '../hooks/useAudioRecorder'
import './HiFiVisualizer.css'
import './Tape.css'
import './Counter.css'

import deckRed from './deckred.jpg'
import deckBlue from './deckblue.jpg'
import deckGreen from './deckgreen.jpg'
import deckEmerald from './deckemerald.jpg'
import deckAmber from './deckamber.jpg'
import deckOrange from './deckorange.jpg'
import deckPale from './deckpale.jpg'
import deckOff from './deckoff.jpg'
import deckOverlay from './deckOverlay.generated'

import {
  WAVES_SCALE,
  BARS_SCALE,
  LINE_SCALE,
  ANALOG_SCALE_LEFT,
  ANALOG_SCALE_RIGHT,
  SPEC_SCALE,
  SHOCK_SCALE,
  MATRIX_SCALE,
  THICK_BARS,
} from '../constants/visualizer'
import { preferBoomAudio } from '../audio/preferBoomAudio'
import {
  deliveredStereoChannels,
  peekChannelCount,
  stereoInputConstraints,
} from '../audio/stereoInput'
import {
  holdInput,
  resumeAudioContext,
  releaseAudioContextResume,
  SharedInput,
  SharedInputContext,
} from '../audio/sharedInput'

// Theme Definitions
const THEMES = {
  red: {
    label: 'Red',
    deckImage: deckRed,
    waveformColor: '#FF003E',
    bandColor: '100, 50, 50',
    waveformGradient: { top: '#1a0505', bottom: '#0a0202' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 100 + 155 * (i / n) + v / 2
      const g = 50 * (i / n)
      const b = thick ? 40 : 20
      return `rgb(${r},${g},${b})`
    },
  },
  blue: {
    label: 'Blue',
    deckImage: deckBlue,
    waveformColor: '#00ccff',
    bandColor: '20, 60, 120',
    waveformGradient: { top: '#05101a', bottom: '#02050a' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 20 * (i / n)
      const g = 80 + 100 * (i / n) + v / 2
      const b = 180 + 75 * (i / n)
      return `rgb(${r},${g},${b})`
    },
  },
  green: {
    label: 'Green',
    deckImage: deckGreen,
    waveformColor: '#00ff00',
    bandColor: '30, 100, 30',
    waveformGradient: { top: '#081a08', bottom: '#020a02' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 10 * (i / n)
      const g = 50 + 205 * (i / n) + v / 2
      const b = thick ? 40 : 20
      return `rgb(${r},${g},${b})`
    },
  },
  emerald: {
    label: 'Emerald',
    deckImage: deckEmerald,
    waveformColor: '#00ffaa',
    bandColor: '20, 100, 90',
    waveformGradient: { top: '#051a15', bottom: '#020a08' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 10 * (i / n)
      const g = 100 + 155 * (i / n) + v / 3
      const b = 100 + 100 * (i / n)
      return `rgb(${r},${g},${b})`
    },
  },
  amber: {
    label: 'Amber',
    deckImage: deckAmber,
    waveformColor: '#FFD715',
    bandColor: '120, 80, 40',
    waveformGradient: { top: '#211A12', bottom: '#0B0906' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 120 + 135 * (i / n) + v / 2
      const g = 80 + 100 * (i / n) + v / 3
      const b = 40 + 20 * (i / n)
      return `rgb(${r},${g},${b})`
    },
  },
  orange: {
    label: 'Orange',
    deckImage: deckOrange,
    waveformColor: '#FF5A00',
    bandColor: '150, 55, 15',
    waveformGradient: { top: '#1c0c04', bottom: '#0a0402' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 180 + 75 * (i / n) + v / 3
      const g = 40 + 90 * (i / n) + v / 4
      const b = thick ? 16 : 8
      return `rgb(${r},${g},${b})`
    },
  },
  pale: {
    label: 'Pale',
    deckImage: deckPale,
    waveformColor: '#E8E4C9',
    bandColor: '100, 95, 70',
    waveformGradient: { top: '#1a1914', bottom: '#0a0907' },
    getBarColor: (i: number, n: number, v: number, thick: boolean) => {
      const r = 100 + 132 * (i / n) + v / 3
      const g = 95 + 133 * (i / n) + v / 3
      const b = 70 + 80 * (i / n) + v / 4
      return `rgb(${r},${g},${b})`
    },
  },
}

type ThemeKey = keyof typeof THEMES

// Theme hex is the look at 25%. 0% is black. 100% matches the old 85% brightness.
const hexChannels = (hex: string) => {
  const value = parseInt(hex.slice(1), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255] as const
}

const shadeBackground = (hex: string, percent: number) => {
  const value = parseInt(hex.slice(1), 16)
  const scale = (percent / 25) * 0.85
  const channel = (shift: number) =>
    Math.max(0, Math.min(255, Math.round(((value >> shift) & 255) * scale)))
  return `rgb(${channel(16)},${channel(8)},${channel(0)})`
}

const SIGNAL_BOOST_SCALE = '-50-50-boost-80'
const clampSignalBoost = (value: number) => Math.max(-50, Math.min(50, value))

const PHOSPHOR_TAU_MS = 50
// After stop, keep drawing silence until bars, dots, and peaks have fallen.
const SILENCE_QUIET_HOLD_MS = 400
const SILENCE_MAX_MS = 8000

// Spectrum averaging, in the analyser's own per-call units. CLASSIC is the Web
// Audio default: a ~75 ms time constant at 60 fps, so bars need ~220 ms to
// follow a transient. FAST still filters single-frame FFT noise but tracks the
// signal in ~20 ms. Both figures scale with frame rate, which is why the deck
// exposes this as a two-position key rather than a dial in milliseconds.
const ANALYSER_SMOOTHING_FAST = 0.25
const ANALYSER_SMOOTHING_CLASSIC = 0.8
const smoothingFor = (fast: boolean) =>
  fast ? ANALYSER_SMOOTHING_FAST : ANALYSER_SMOOTHING_CLASSIC

// Spectrogram colors, keyed by bin and quantized lightness. Cells are filled
// on first use, so only the combinations the signal actually reaches exist.
const SPEC_LIGHTNESS_STEPS = 64
let specPaletteBins = 0
let specPaletteCache: Array<string | undefined> = []
// Falling peak row for the LED matrix, kept per canvas so stereo channels
// don't share one hold.
const matrixPeakCache = new WeakMap<HTMLCanvasElement, Float32Array>()

const specPalette = (bins: number) => {
  if (specPaletteBins !== bins) {
    specPaletteBins = bins
    specPaletteCache = new Array(bins * SPEC_LIGHTNESS_STEPS)
  }
  return specPaletteCache
}

const readStoredSignalBoost = () => {
  const saved = localStorage.getItem('knobSignalValue')
  if (saved === null) return 0
  const parsed = parseFloat(saved)
  if (isNaN(parsed)) return 0
  const scale = localStorage.getItem('knobSignalScale')
  if (scale === SIGNAL_BOOST_SCALE) return clampSignalBoost(parsed)
  // Previous 0-100 boost-80: 0 stays 0, 100 maps to 50.
  if (scale === '0-100-boost-80') return clampSignalBoost(parsed * 0.5)
  // Previous 0-100 boost used 100 as 5x. 80 on that scale is 50 on this one.
  if (scale === '0-100-boost') return clampSignalBoost((parsed / 0.8) * 0.5)
  // Previous 0-100 scale used 50 as unity.
  if (scale === '0-100') return clampSignalBoost(parsed - 50)
  // Oldest bipolar -50..50 scale used 0 as unity.
  return clampSignalBoost(parsed)
}

const signalBoostToCoefficient = (boost: number) =>
  Math.pow(5, (boost / 50) * 0.8)

// Thin lines read as gray because each pixel is blended with the dark background.
// Push the color away from its gray average so the hue stays and the chroma rises.
const saturateLineColor = (color: string) => {
  const match = color.match(/rgb\(([\d.]+),\s*([\d.]+),\s*([\d.]+)\)/)
  if (!match) return color
  const channels = [Number(match[1]), Number(match[2]), Number(match[3])]
  const average = (channels[0] + channels[1] + channels[2]) / 3
  const amount = 1.9
  const next = channels.map((channel) =>
    Math.max(
      0,
      Math.min(255, Math.round(average + (channel - average) * amount))
    )
  )
  return `rgb(${next[0]},${next[1]},${next[2]})`
}

// Bar color depends on theme, bin, and height. Quantize height so the string
// is built once per step instead of on every frame.
const BAR_COLOR_LEVELS = 24
const BAR_COLOR_STEP = 8
let barColorTheme: ThemeKey | null = null
let barColorThick = false
let barColorBins = 0
let barColorCache: Array<string | undefined> = []
const barFill = (
  themeKey: ThemeKey,
  i: number,
  n: number,
  height: number,
  thick: boolean,
  mono: boolean,
  themeColor: string
) => {
  if (mono) return themeColor
  if (
    barColorTheme !== themeKey ||
    barColorThick !== thick ||
    barColorBins !== n
  ) {
    barColorTheme = themeKey
    barColorThick = thick
    barColorBins = n
    barColorCache = new Array(n * BAR_COLOR_LEVELS)
  }
  const level = Math.max(
    0,
    Math.min(BAR_COLOR_LEVELS - 1, (height / BAR_COLOR_STEP) | 0)
  )
  const slot = i * BAR_COLOR_LEVELS + level
  let color = barColorCache[slot]
  if (color === undefined) {
    color = THEMES[themeKey].getBarColor(i, n, level * BAR_COLOR_STEP, thick)
    if (!thick) color = saturateLineColor(color)
    barColorCache[slot] = color
  }
  return color
}

const readStoredBackground = () => {
  const saved = localStorage.getItem('visualizerBgLevel')
  if (saved !== null) {
    const parsed = parseFloat(saved)
    if (!isNaN(parsed)) return Math.max(0, Math.min(100, parsed))
  }
  return 25
}

type VisualizationType =
  | 'bars'
  | 'waveform'
  | 'thickBars'
  | 'analog'
  | 'spectrogram'
  | 'shockwaves'
  | 'ledMatrix'
  | 'neonRibbon'

type WidgetButton = 'waves' | 'bars' | 'analog'
type FxMode = 'spectrogram' | 'shockwaves' | 'lines'

const VISUALIZATION_TYPES: VisualizationType[] = [
  'bars',
  'waveform',
  'thickBars',
  'analog',
  'spectrogram',
  'shockwaves',
  'ledMatrix',
  'neonRibbon',
]

const readStoredVisualization = () => {
  const savedType = localStorage.getItem('currentVisualization')
  const savedFx = localStorage.getItem('currentFx')
  const savedWidget = localStorage.getItem('currentWidget')
  const visualizationType = VISUALIZATION_TYPES.find((type) => type === savedType)
  const fxMode: FxMode | null =
    savedFx === 'spectrogram' || savedFx === 'shockwaves' || savedFx === 'lines'
      ? savedFx
      : null
  const activeButton: WidgetButton | null =
    savedWidget === 'waves' || savedWidget === 'bars' || savedWidget === 'analog'
      ? savedWidget
      : null

  if (visualizationType) {
    return { visualizationType, fxMode, activeButton }
  }

  if (savedWidget === 'waves') {
    return {
      visualizationType: 'waveform' as VisualizationType,
      fxMode: null,
      activeButton: 'waves' as WidgetButton,
    }
  }
  if (savedWidget === 'bars') {
    return {
      visualizationType: 'thickBars' as VisualizationType,
      fxMode: null,
      activeButton: 'bars' as WidgetButton,
    }
  }
  return {
    visualizationType: 'analog' as VisualizationType,
    fxMode: null,
    activeButton: 'analog' as WidgetButton,
  }
}

interface HiFiVisualizerProps {
  visualizationType?: 'bars' | 'waveform' | 'thickBars' | 'analog'
}

const HiFiVisualizer: React.FC<HiFiVisualizerProps> = () => {
  const [activeButton, setActiveButton] = useState<WidgetButton | null>(
    () => readStoredVisualization().activeButton
  )
  const [visualizationType, setVisualizationType] = useState<VisualizationType>(
    () => readStoredVisualization().visualizationType
  )

  const [isListening, setIsListening] = useState(false)
  // FX mode state
  const [fxMode, setFxMode] = useState<FxMode | null>(
    () => readStoredVisualization().fxMode
  )
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('')
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([])
  const [isStereo] = useState(true)
  const [displayMode, setDisplayMode] = useState<'stereo' | 'mono'>('stereo')
  const displayModeRef = useRef<'stereo' | 'mono'>('stereo')
  displayModeRef.current = displayMode
  const knobRawValue = readStoredSignalBoost()
  const backgroundLevelRef = useRef(readStoredBackground())
  const scaleCoefficientRef = useRef(signalBoostToCoefficient(knobRawValue))
  const [waveformGlow, setWaveformGlow] = useState(() =>
    localStorage.getItem('glowGlobalState') === 'true'
  )
  const waveformGlowRef = useRef(
    localStorage.getItem('glowGlobalState') === 'true'
  )
  const [phosphorEnabled, setPhosphorEnabled] = useState(() =>
    localStorage.getItem('phosphorGlobalState') === 'true'
  )
  const phosphorEnabledRef = useRef(
    localStorage.getItem('phosphorGlobalState') === 'true'
  )
  // Snappy tracking is the default; the key switches back to the softer
  // averaging the deck shipped with.
  const [fastResponse, setFastResponse] = useState(
    () => localStorage.getItem('fastResponseState') !== 'false'
  )
  const fastResponseRef = useRef(
    localStorage.getItem('fastResponseState') !== 'false'
  )
  const [monoColor, setMonoColor] = useState(
    () => localStorage.getItem('monoColor') === 'true'
  )
  const monoColorRef = useRef(localStorage.getItem('monoColor') === 'true')
  const [deckPowered, setDeckPowered] = useState(true)
  const isListeningRef = useRef(isListening)
  isListeningRef.current = isListening
  const deckPoweredRef = useRef(deckPowered)
  deckPoweredRef.current = deckPowered
  const vizSessionRef = useRef<{
    mute: () => void
    teardown: () => void
  } | null>(null)
  const [leftWheelStart] = useState(() => Math.floor(Math.random() * 360))
  const [rightWheelStart] = useState(() => Math.floor(Math.random() * 360))

  useEffect(() => {
    const turnOn = () => setDeckPowered(true)
    turnOn()
    window.addEventListener('pageshow', turnOn)
    return () => window.removeEventListener('pageshow', turnOn)
  }, [])

  // Peak falling effect state
  const [showPeaks, setShowPeaks] = useState(true)
  const showPeaksRef = useRef(true)

  useEffect(() => {
    showPeaksRef.current = showPeaks
  }, [showPeaks])

  useEffect(() => {
    waveformGlowRef.current = waveformGlow
    localStorage.setItem('glowGlobalState', String(waveformGlow))
  }, [waveformGlow])

  useEffect(() => {
    phosphorEnabledRef.current = phosphorEnabled
    localStorage.setItem('phosphorGlobalState', String(phosphorEnabled))
  }, [phosphorEnabled])

  useEffect(() => {
    fastResponseRef.current = fastResponse
    localStorage.setItem('fastResponseState', String(fastResponse))
  }, [fastResponse])

  useEffect(() => {
    monoColorRef.current = monoColor
    localStorage.setItem('monoColor', String(monoColor))
  }, [monoColor])

  useEffect(() => {
    localStorage.setItem('currentVisualization', visualizationType)
    localStorage.setItem('currentFx', fxMode ?? '')
    localStorage.setItem('currentWidget', activeButton ?? '')
  }, [visualizationType, fxMode, activeButton])

  // Log visualization type changes
  useEffect(() => {
    console.log('📊 Current Visualization:', visualizationType)
  }, [visualizationType])

  // Canvas refs for left and right visualizers
  const leftCanvasRef = useRef<HTMLCanvasElement>(null)
  const rightCanvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    if (deckPowered) return
    const leftCanvas = leftCanvasRef.current
    const rightCanvas = rightCanvasRef.current
    leftCanvas
      ?.getContext('2d')
      ?.clearRect(0, 0, leftCanvas.width, leftCanvas.height)
    rightCanvas
      ?.getContext('2d')
      ?.clearRect(0, 0, rightCanvas.width, rightCanvas.height)
  }, [deckPowered])
  const audioContextRef = useRef<AudioContext | null>(null)
  const primedContextRef = useRef<AudioContext | null>(null)
  const [sharedInput, setSharedInput] = useState<SharedInput | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  // Stereo split refs
  const splitterRef = useRef<ChannelSplitterNode | null>(null)
  const analyserLRef = useRef<AnalyserNode | null>(null)
  const analyserRRef = useRef<AnalyserNode | null>(null)
  const analyserMonoRef = useRef<AnalyserNode | null>(null)

  // Writable on a live node. Toggling does not rebuild the graph.
  useEffect(() => {
    const smoothing = smoothingFor(fastResponse)
    const nodes = [
      analyserLRef.current,
      analyserRRef.current,
      analyserMonoRef.current,
      analyserRef.current,
    ]
    for (const node of nodes) {
      if (node) node.smoothingTimeConstant = smoothing
    }
  }, [fastResponse])

  // Effect state refs
  const shockwaveRingsLRef = useRef<
    Array<{
      x: number
      y: number
      radius: number
      life: number
      intensity: number
    }>
  >([])
  const shockwaveRingsRRef = useRef<
    Array<{
      x: number
      y: number
      radius: number
      life: number
      intensity: number
    }>
  >([])
  const lastBeatTimeLRef = useRef<number>(0)
  const lastBeatTimeRRef = useRef<number>(0)

  // Peak bars state
  const peaksLRef = useRef<Float32Array | null>(null)
  const peaksRRef = useRef<Float32Array | null>(null)

  // Calibrations State
  const [isCalibrating] = useState(false)

  // Draggable panel state
  const [panelPosition, setPanelPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const panelRef = useRef<HTMLDivElement>(null)

  // Drawing mode state
  const [isDrawingMode, setIsDrawingMode] = useState(false)
  const [drawingRect, setDrawingRect] = useState<{
    startX: number
    startY: number
    endX: number
    endY: number
    isDrawing: boolean
  } | null>(null)
  const [drawnRectangles, setDrawnRectangles] = useState<
    Array<{
      x: number
      y: number
      width: number
      height: number
      element: string
    }>
  >([])
  const [targetElement, setTargetElement] = useState<string>('custom-1')
  const containerRef = useRef<HTMLDivElement>(null)
  const [copySuccess, setCopySuccess] = useState(false)

  // Custom elements state
  const DEFAULT_CUSTOM_ELEMENTS = [
    {
      id: 'custom-1',
      name: 'Custom Element 1',
      top: 94.57536565368034,
      left: 41.409691629955944,
      width: 0,
      height: 0,
    },
  ]
  const [customElements, setCustomElements] = useState<
    Array<{
      id: string
      name: string
      top?: number
      left?: number
      right?: number
      bottom?: number
      width?: number
      height?: number
    }>
  >([...DEFAULT_CUSTOM_ELEMENTS])
  const [nextCustomId, setNextCustomId] = useState(2)

  // Theme State
  const [currentTheme, setCurrentTheme] = useState<ThemeKey>(() => {
    const saved = localStorage.getItem('currentColor')
    const validThemes: ThemeKey[] = [
      'red',
      'blue',
      'green',
      'emerald',
      'amber',
      'orange',
    ]
    return saved && validThemes.includes(saved as ThemeKey)
      ? (saved as ThemeKey)
      : 'blue'
  })
  const visualizationTypeRef = useRef(visualizationType)
  visualizationTypeRef.current = visualizationType
  const currentThemeRef = useRef(currentTheme)
  currentThemeRef.current = currentTheme
  const theme = THEMES[currentTheme]
  const themeChannels = hexChannels(theme.waveformColor)
  const themeRgb = `${themeChannels[0]}, ${themeChannels[1]}, ${themeChannels[2]}`

  // Theme cycling functions
  const themeKeys = Object.keys(THEMES) as ThemeKey[]
  const cycleThemeNext = () => {
    const currentIndex = themeKeys.indexOf(currentTheme)
    const nextIndex = (currentIndex + 1) % themeKeys.length
    setCurrentTheme(themeKeys[nextIndex])
  }

  // Save current color to localStorage
  useEffect(() => {
    localStorage.setItem('currentColor', currentTheme)
  }, [currentTheme])

  const paintBackground = (level: number, themeKey: ThemeKey = currentTheme) => {
    const root = containerRef.current
    if (!root) return
    const next = THEMES[themeKey]
    root.style.setProperty(
      '--top-color',
      shadeBackground(next.waveformGradient.top, level)
    )
    root.style.setProperty(
      '--bottom-color',
      shadeBackground(next.waveformGradient.bottom, level)
    )
  }

  useEffect(() => {
    paintBackground(backgroundLevelRef.current, currentTheme)
  }, [currentTheme])

  // FX mode cycling functions
  // Direct FX mode selection functions
  const selectSpectrogram = () => {
    if (fxMode === 'spectrogram') return
    setFxMode('spectrogram')
    setVisualizationType('spectrogram')
    setActiveButton(null)
  }

  const selectDots = () => {
    if (fxMode === 'shockwaves') {
      setVisualizationType((current) =>
        current === 'ledMatrix' ? 'shockwaves' : 'ledMatrix'
      )
      return
    }
    setFxMode('shockwaves')
    setVisualizationType('ledMatrix')
    setActiveButton(null)
  }

  const selectLines = () => {
    if (fxMode === 'lines') {
      setShowPeaks((on) => !on)
      return
    }
    setFxMode('lines')
    setVisualizationType('bars')
    setShowPeaks(true)
    setActiveButton(null)
  }

  // Get audio devices
  const getAudioDevices = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioInputs = preferBoomAudio(
        devices.filter((device) => device.kind === 'audioinput')
      )
      setAudioDevices(audioInputs)
      setSelectedDeviceId(
        (current) => current || audioInputs[0]?.deviceId || ''
      )
    } catch (error) {
      console.error('Error listing audio devices:', error)
    }
  }, [])

  useEffect(() => {
    getAudioDevices()
    navigator.mediaDevices.addEventListener('devicechange', getAudioDevices)
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        getAudioDevices
      )
    }
  }, [getAudioDevices])

  // Lazy preload deck images after initial render (non-blocking)
  useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout
    const schedulePreload = (callback: () => void) => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(callback, { timeout: 2000 })
      } else {
        setTimeout(callback, 100)
      }
    }

    schedulePreload(() => {
      const deckImages = [
        deckRed,
        deckBlue,
        deckGreen,
        deckEmerald,
        deckAmber,
        deckOrange,
        deckPale,
        deckOff,
      ]
      deckImages.forEach((imageSrc) => {
        const img = new Image()
        img.src = imageSrc
      })
    })
  }, [])

  // Main audio visualization loop
  useEffect(() => {
    const leftCanvas = leftCanvasRef.current
    const rightCanvas = rightCanvasRef.current
    if (!leftCanvas || !rightCanvas) return

    const leftCtx = leftCanvas.getContext('2d')
    const rightCtx = rightCanvas.getContext('2d')
    if (!leftCtx || !rightCtx) return

    let animationFrameId: number | null = null
    let isActive = true
    let cleaned = false
    let silent = false
    let silentElapsedMs = 0
    let quietMs = 0
    let retainedInput: SharedInput | null = null

    // One buffer per domain instead of a fresh typed array on every frame of
    // every channel. Each draw reads the analyser and consumes the data before
    // returning, so both channels can share them.
    let freqScratch: Uint8Array<ArrayBuffer> | null = null
    const freqBuffer = (length: number) => {
      if (!freqScratch || freqScratch.length !== length) {
        freqScratch = new Uint8Array(length)
      }
      return freqScratch
    }
    let timeScratch: Float32Array<ArrayBuffer> | null = null
    const timeBuffer = (length: number) => {
      if (!timeScratch || timeScratch.length !== length) {
        timeScratch = new Float32Array(length)
      }
      return timeScratch
    }
    const readFreq = (
      analyser: AnalyserNode,
      dataArray: Uint8Array<ArrayBuffer>
    ) => {
      if (silent) dataArray.fill(0)
      else analyser.getByteFrequencyData(dataArray)
    }
    const readTime = (
      analyser: AnalyserNode,
      dataArray: Float32Array<ArrayBuffer>
    ) => {
      if (silent) dataArray.fill(0)
      else analyser.getFloatTimeDomainData(dataArray)
    }
    const peakMax = (peaks: Float32Array | null | undefined) => {
      if (!peaks) return 0
      let max = 0
      for (let i = 0; i < peaks.length; i++) {
        if (peaks[i] > max) max = peaks[i]
      }
      return max
    }
    const isVisuallyQuiet = () => {
      const mode = visualizationTypeRef.current
      if (
        mode === 'bars' ||
        mode === 'thickBars' ||
        mode === 'neonRibbon'
      ) {
        return (
          peakMax(peaksLRef.current) <= 0.5 &&
          peakMax(peaksRRef.current) <= 0.5
        )
      }
      if (mode === 'shockwaves') {
        return (
          shockwaveRingsLRef.current.length === 0 &&
          shockwaveRingsRRef.current.length === 0
        )
      }
      if (mode === 'ledMatrix') {
        return (
          peakMax(matrixPeakCache.get(leftCanvas)) <= 0.05 &&
          peakMax(matrixPeakCache.get(rightCanvas)) <= 0.05
        )
      }
      return true
    }
    // Only the effect that started listening may close a context that never
    // became shared. The previous effect's cleanup runs after the click that
    // primed it, and must leave that context running.
    const releaseUnpublishedPrime = isListening

    const setupAudio = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: stereoInputConstraints(selectedDeviceId),
        })
        if (!isActive) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        // Permission unlocks device IDs and labels, but may not emit devicechange.
        getAudioDevices()
        const track = stream.getAudioTracks()[0]
        // Most stereo devices already report 2 channels. Only wait on
        // applyConstraints when the first peek is still mono.
        let deliveredChannels = peekChannelCount(track)
        if (deliveredChannels < 2) {
          deliveredChannels = await deliveredStereoChannels(track)
          if (!isActive) {
            stream.getTracks().forEach((track) => track.stop())
            return
          }
        }
        let context = primedContextRef.current
        if (!context || context.state === 'closed') {
          context = new (
            window.AudioContext || (window as any).webkitAudioContext
          )()
          primedContextRef.current = context
        }
        resumeAudioContext(context)
        audioContextRef.current = context
        const source = context.createMediaStreamSource(stream)
        sourceRef.current = source

        if (isStereo && deliveredChannels >= 2) {
          // Stereo mode: split channels
          splitterRef.current = context.createChannelSplitter(2)
          source.connect(splitterRef.current)

          analyserLRef.current = context.createAnalyser()
          analyserLRef.current.fftSize = 2048
          analyserLRef.current.smoothingTimeConstant = smoothingFor(
            fastResponseRef.current
          )
          splitterRef.current.connect(analyserLRef.current, 0)

          analyserRRef.current = context.createAnalyser()
          analyserRRef.current.fftSize = 2048
          analyserRRef.current.smoothingTimeConstant = smoothingFor(
            fastResponseRef.current
          )
          splitterRef.current.connect(analyserRRef.current, 1)

          // Keep legacy analyser for backward compatibility with existing modes
          analyserRef.current = analyserLRef.current
        } else {
          // Mono input: use single analyser
          analyserRef.current = context.createAnalyser()
          analyserRef.current.fftSize = 2048
          analyserRef.current.smoothingTimeConstant = smoothingFor(
            fastResponseRef.current
          )
          source.connect(analyserRef.current)
          analyserLRef.current = analyserRef.current
          analyserRRef.current = null // Will duplicate left for right
        }

        // Downmix for the single wide screen. Toggling display mode does not
        // rebuild this graph.
        analyserMonoRef.current = context.createAnalyser()
        analyserMonoRef.current.fftSize = 2048
        analyserMonoRef.current.smoothingTimeConstant = smoothingFor(
          fastResponseRef.current
        )
        analyserMonoRef.current.channelCount = 1
        analyserMonoRef.current.channelCountMode = 'explicit'
        analyserMonoRef.current.channelInterpretation = 'speakers'
        source.connect(analyserMonoRef.current)

        // Short-persistence phosphor. Brief traces fade out instead of
        // vanishing on the next frame. Pixels are erased toward transparent
        // so the theme gradient shows through.
        let lastDrawTime = 0

        const fadePhosphor = (
          ctx: CanvasRenderingContext2D,
          canvas: HTMLCanvasElement,
          dtMs: number
        ) => {
          const amount = 1 - Math.exp(-dtMs / PHOSPHOR_TAU_MS)
          ctx.save()
          ctx.globalCompositeOperation = 'destination-out'
          ctx.fillStyle = `rgba(0, 0, 0, ${amount})`
          ctx.fillRect(0, 0, canvas.width, canvas.height)
          ctx.restore()
        }

        const drawChannel = (
          ctx: CanvasRenderingContext2D,
          canvas: HTMLCanvasElement,
          channelAnalyser: AnalyserNode | null,
          peaksRef: typeof peaksLRef,
          ringsRef: typeof shockwaveRingsLRef,
          beatRef: typeof lastBeatTimeLRef,
          dtMs: number
        ) => {
          if (!channelAnalyser) return
          const persist = () =>
            phosphorEnabledRef.current
              ? fadePhosphor(ctx, canvas, dtMs)
              : ctx.clearRect(0, 0, canvas.width, canvas.height)
          const mode = visualizationTypeRef.current
          if (mode === 'waveform') {
            persist()
            drawWaveform(ctx, channelAnalyser, canvas.width, canvas.height)
          } else if (mode === 'bars') {
            persist()
            drawBars(
              ctx,
              channelAnalyser,
              canvas.width,
              canvas.height,
              false,
              peaksRef
            )
          } else if (mode === 'thickBars') {
            persist()
            drawBars(
              ctx,
              channelAnalyser,
              canvas.width,
              canvas.height,
              true,
              peaksRef,
              canvas.width > 400
            )
          } else if (mode === 'spectrogram') {
            drawSpectrogram(
              ctx,
              canvas,
              channelAnalyser,
              canvas.width,
              canvas.height
            )
          } else if (mode === 'shockwaves') {
            persist()
            drawShockwaves(
              ctx,
              channelAnalyser,
              ringsRef,
              beatRef,
              canvas.width,
              canvas.height
            )
          } else if (mode === 'ledMatrix') {
            ctx.clearRect(0, 0, canvas.width, canvas.height)
            drawLedMatrix(ctx, canvas, channelAnalyser, dtMs)
          } else if (mode === 'neonRibbon') {
            persist()
            drawBars(
              ctx,
              channelAnalyser,
              canvas.width,
              canvas.height,
              false,
              peaksRef,
              canvas.width > 400
            )
          }
        }

        const draw = () => {
          if (!leftCanvas || !rightCanvas || !leftCtx || !rightCtx) return

          const now = performance.now()
          const dtMs =
            lastDrawTime === 0 ? 16 : Math.min(100, now - lastDrawTime)
          lastDrawTime = now

          const analyserL = analyserLRef.current
          const analyserR = analyserRRef.current || analyserLRef.current // Duplicate left if mono input
          const wideMono =
            displayModeRef.current === 'mono' &&
            visualizationTypeRef.current !== 'analog'

          if (visualizationTypeRef.current !== 'analog') {
            if (wideMono) {
              drawChannel(
                leftCtx,
                leftCanvas,
                analyserMonoRef.current,
                peaksLRef,
                shockwaveRingsLRef,
                lastBeatTimeLRef,
                dtMs
              )
            } else if (analyserL) {
              drawChannel(
                leftCtx,
                leftCanvas,
                analyserL,
                peaksLRef,
                shockwaveRingsLRef,
                lastBeatTimeLRef,
                dtMs
              )
              drawChannel(
                rightCtx,
                rightCanvas,
                analyserR,
                peaksRRef,
                shockwaveRingsRRef,
                lastBeatTimeRRef,
                dtMs
              )
            }
          }

          retainedInput?.tick()
          if (silent) {
            silentElapsedMs += dtMs
            if (isVisuallyQuiet()) quietMs += dtMs
            else quietMs = 0
            if (
              quietMs >= SILENCE_QUIET_HOLD_MS ||
              silentElapsedMs >= SILENCE_MAX_MS
            ) {
              cleanupAudio()
              return
            }
          }
          animationFrameId = requestAnimationFrame(draw)
        }

        const held = holdInput(context, stream, source, deliveredChannels, {
          splitter: splitterRef.current,
          scaleRef: scaleCoefficientRef,
          fastRef: fastResponseRef,
        })
        held.retain()
        retainedInput = held
        vizSessionRef.current = {
          mute: () => {
            silent = true
            held.mute()
          },
          teardown: cleanupAudio,
        }
        setSharedInput(held)
        draw()
      } catch (error) {
        console.error('Error accessing audio device:', error)
      }
    }

    const drawWaveform = (
      ctx: CanvasRenderingContext2D,
      analyser: AnalyserNode,
      WIDTH: number,
      HEIGHT: number
    ) => {
      const bufferLength = analyser.fftSize
      const dataArray = timeBuffer(bufferLength)
      readTime(analyser, dataArray)

      const waveformColor = THEMES[currentThemeRef.current].waveformColor

      // Set up glow effects only if enabled
      if (waveformGlowRef.current) {
        ctx.shadowBlur = 20
        ctx.shadowColor = waveformColor
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = 0
      }

      ctx.lineWidth = 2
      ctx.strokeStyle = waveformColor
      ctx.beginPath()

      const verticalOffset = HEIGHT / 4
      const points = Math.max(1, WIDTH)
      const sampleStep = bufferLength / points

      for (let x = 0; x < points; x++) {
        const v = dataArray[(x * sampleStep) | 0] * 0.5 + 0.5
        const scaledV =
          (v - 0.5) * WAVES_SCALE * scaleCoefficientRef.current + 0.5
        const y = scaledV * (HEIGHT / 2) + verticalOffset

        if (x === 0) {
          ctx.moveTo(x, y)
        } else {
          ctx.lineTo(x, y)
        }
      }

      if (waveformGlowRef.current) {
        // Draw multiple strokes with increasing glow for stronger effect
        // First pass: strong glow
        ctx.shadowBlur = 30
        ctx.stroke()

        // Second pass: medium glow
        ctx.shadowBlur = 15
        ctx.stroke()

        // Third pass: base line with minimal glow
        ctx.shadowBlur = 5
        ctx.stroke()

        // Reset shadow
        ctx.shadowBlur = 0
      } else {
        // Single stroke without glow
        ctx.stroke()
      }
    }

    const drawBars = (
      ctx: CanvasRenderingContext2D,
      analyser: AnalyserNode,
      WIDTH: number,
      HEIGHT: number,
      thick: boolean,
      peaksRef: React.MutableRefObject<Float32Array | null>,
      wide = false
    ) => {
      const bufferLength = thick ? THICK_BARS : analyser.frequencyBinCount
      const dataArray = freqBuffer(bufferLength)
      readFreq(analyser, dataArray)

      // Initialize or resize peaks array
      if (!peaksRef.current || peaksRef.current.length !== bufferLength) {
        peaksRef.current = new Float32Array(bufferLength)
      }
      const peaks = peaksRef.current
      const decayRate = thick ? 1.0 : 1.5 // Slower decay for thick bars

      const barWidth = (WIDTH / bufferLength) * (thick ? 0.92 : 2.5)
      // Wide mono bars keep a 3px gap and the last one ends on the canvas edge.
      const monoGap = 3
      const monoSegmentWidth = wide
        ? (WIDTH - monoGap * (bufferLength - 1)) / bufferLength
        : 0
      let x = 0

      // Thin bars advance faster than their width, so the high bins land past
      // the canvas edge. Drawing them cost ~80% of this loop for no pixels.
      const barStep =
        thick && wide ? monoSegmentWidth + monoGap : barWidth + (thick ? 2 : 1)
      const visibleBars = Math.min(
        bufferLength,
        Math.max(1, Math.ceil(WIDTH / barStep))
      )
      // A later width change must not reveal peaks left behind off-screen.
      peaks.fill(0, visibleBars)

      // Get theme color for glow
      const themeDef = THEMES[currentThemeRef.current]
      const themeColor = themeDef.waveformColor
      const mono = monoColorRef.current

      // Constant for the whole pass, and shadowed fills are the slowest thing
      // here, so this must not be reassigned per bar.
      if (waveformGlowRef.current) {
        ctx.shadowBlur = 15
        ctx.shadowColor = themeColor
        ctx.shadowOffsetX = 0
        ctx.shadowOffsetY = 0
      } else {
        ctx.shadowBlur = 0
      }

      for (let i = 0; i < visibleBars; i++) {
        const barHeight =
          (dataArray[i] / 2) *
          (thick ? BARS_SCALE : LINE_SCALE) *
          scaleCoefficientRef.current

        // Update peak
        if (barHeight >= peaks[i]) {
          peaks[i] = barHeight
        } else {
          peaks[i] = Math.max(0, peaks[i] - decayRate)
        }

        const peakHeightVal = peaks[i]

        // One theme color for every bar, or the per-bar gradient.
        const barColor = barFill(
          currentThemeRef.current,
          i,
          bufferLength,
          barHeight,
          thick,
          mono,
          themeColor
        )
        ctx.fillStyle = barColor

        if (thick) {
          // Draw stacked rectangles for thick bars
          // 8:6 aspect ratio (width:height)
          const segmentHeight = 4 // Height of each rectangle segment
          // Stereo keeps a fixed 11px block. Wide mono bars fill the screen.
          const segmentWidth = wide ? monoSegmentWidth : 11
          const segmentGap = 2 // Gap between segments (smaller for more segments)
          const numSegments = Math.floor(
            barHeight / (segmentHeight + segmentGap)
          )

          const segmentX = wide ? x : x + (barWidth - segmentWidth) / 2

          for (let seg = 0; seg < numSegments; seg++) {
            const segY = HEIGHT - (seg + 1) * (segmentHeight + segmentGap)
            ctx.fillRect(segmentX, segY, segmentWidth, segmentHeight)
          }

          // Draw Peak Segment
          if (showPeaksRef.current && peakHeightVal > 0) {
            // For thick bars, we can make the peak snapped or smooth.
            // Let's try smooth but maintain segment size.
            // Calculated Y based on peak value
            const peakY = HEIGHT - peakHeightVal - segmentHeight

            // Ensure it doesn't overlap with the top segment if barHeight is close to peak
            // Actually overlapping slightly is fine or being exactly on top.
            // If we want it to look like a separated segment, we might want to ensure a gap.
            // But if peak == barHeight, it should look attached.

            ctx.fillStyle = barColor // Use same color
            ctx.fillRect(segmentX, peakY, segmentWidth, segmentHeight)
          }
        } else {
          // Draw solid bar for thin bars
          ctx.fillRect(x, HEIGHT - barHeight, barWidth, barHeight)

          // Draw Peak Line
          if (showPeaksRef.current && peakHeightVal > 0) {
            const peakY = HEIGHT - peakHeightVal
            ctx.fillStyle = barColor
            // Draw a 2px high peak line
            ctx.fillRect(x, peakY - 3, barWidth, 2)
          }
        }

        x += barStep
      }

      // Reset shadow after drawing
      ctx.shadowBlur = 0
    }

    // Spectrogram Waterfall: scrolling time-frequency heatmap
    const drawSpectrogram = (
      ctx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      analyser: AnalyserNode,
      WIDTH: number,
      HEIGHT: number
    ) => {
      // Scroll existing content up by 1px
      ctx.drawImage(canvas, 0, -1)

      // Draw new row at bottom
      const bufferLength = analyser.frequencyBinCount
      const dataArray = freqBuffer(bufferLength)
      readFreq(analyser, dataArray)

      // Use only the first 70% of bins (meaningful audio data)
      const usefulBins = Math.floor(bufferLength * 0.7)

      const rowY = HEIGHT - 1
      const barWidth = WIDTH / usefulBins // Stretch across full width
      const palette = specPalette(usefulBins)

      for (let i = 0; i < usefulBins; i++) {
        const value = dataArray[i]
        const intensity = Math.min(
          1,
          (value / 255) * SPEC_SCALE * scaleCoefficientRef.current
        )
        // Quantizing lightness to ~1% steps keeps every color string cached
        // instead of rebuilding and reparsing one per bin per frame.
        const step = Math.round(intensity * (SPEC_LIGHTNESS_STEPS - 1))
        const slot = i * SPEC_LIGHTNESS_STEPS + step
        let color = palette[slot]
        if (color === undefined) {
          const hue = (i / usefulBins) * 240 // 0-240 for blue to red spectrum
          const lightness = 20 + (step / (SPEC_LIGHTNESS_STEPS - 1)) * 60
          color = `hsl(${hue}, 100%, ${lightness}%)`
          palette[slot] = color
        }
        ctx.fillStyle = color
        ctx.fillRect(i * barWidth, rowY, barWidth, 1)
      }
    }

    // Shockwaves: Beat-synced expanding rings
    const drawShockwaves = (
      ctx: CanvasRenderingContext2D,
      analyser: AnalyserNode,
      ringsRef: React.MutableRefObject<
        Array<{
          x: number
          y: number
          radius: number
          life: number
          intensity: number
        }>
      >,
      lastBeatTimeRef: React.MutableRefObject<number>,
      WIDTH: number,
      HEIGHT: number
    ) => {
      const bufferLength = analyser.frequencyBinCount
      const dataArray = freqBuffer(bufferLength)
      readFreq(analyser, dataArray)

      // Detect beat in low frequencies (bass) - use wider range and lower threshold
      const shockGain = SHOCK_SCALE * scaleCoefficientRef.current
      const bassRange = Math.floor(bufferLength * 0.15)
      let bassSum = 0
      for (let i = 0; i < bassRange; i++) bassSum += dataArray[i]
      let totalSum = 0
      for (let i = 0; i < bufferLength; i++) totalSum += dataArray[i]
      const bassEnergy = (bassSum / bassRange / 255) * shockGain
      const currentTime = Date.now()
      const beatThreshold = 0.35 // Lower threshold for more frequent rings

      // Create new ring on beat - also check overall energy for more responsiveness
      const overallEnergy = (totalSum / bufferLength / 255) * shockGain
      const energyThreshold = Math.max(bassEnergy, overallEnergy * 0.7)

      if (
        energyThreshold > beatThreshold &&
        currentTime - lastBeatTimeRef.current > 150 // Shorter cooldown
      ) {
        ringsRef.current.push({
          x: WIDTH / 2,
          y: HEIGHT / 2,
          radius: 0,
          life: 1.0,
          intensity: energyThreshold,
        })
        lastBeatTimeRef.current = currentTime
      }

      // Update and draw rings
      const waveformColor = THEMES[currentThemeRef.current].waveformColor
      ringsRef.current = ringsRef.current.filter((ring) => {
        // Faster expansion
        ring.radius += 5 + ring.intensity * 8
        // Slower fade for longer visibility
        ring.life -= 0.015
        ring.life = Math.max(0, ring.life)

        if (ring.life <= 0 || ring.radius > Math.max(WIDTH, HEIGHT) * 1.5) {
          return false
        }

        // Draw ring with better visibility
        ctx.strokeStyle = waveformColor
        ctx.globalAlpha = ring.life // Full opacity when life is high
        ctx.lineWidth = Math.max(2, 4 * ring.life) // Thicker lines

        // Apply glow based on global glow state
        if (waveformGlowRef.current) {
          ctx.shadowBlur = 30 * ring.life // More glow
          ctx.shadowColor = waveformColor
        } else {
          ctx.shadowBlur = 0
        }

        ctx.beginPath()
        ctx.arc(ring.x, ring.y, ring.radius, 0, Math.PI * 2)
        ctx.stroke()

        // Draw a second inner ring for more visibility
        if (ring.life > 0.5) {
          ctx.globalAlpha = ring.life * 0.5
          ctx.lineWidth = 2

          // Apply glow based on global glow state
          if (waveformGlowRef.current) {
            ctx.shadowBlur = 15 * ring.life
            ctx.shadowColor = waveformColor
          } else {
            ctx.shadowBlur = 0
          }

          ctx.beginPath()
          ctx.arc(ring.x, ring.y, ring.radius * 0.9, 0, Math.PI * 2)
          ctx.stroke()
        }

        return true
      })

      ctx.globalAlpha = 1.0
      ctx.shadowBlur = 0
    }

    // Frequency columns of round LEDs. Unlit dots stay visible so the panel
    // reads as a matrix, and one peak dot falls back down each column.
    const drawLedMatrix = (
      ctx: CanvasRenderingContext2D,
      canvas: HTMLCanvasElement,
      analyser: AnalyserNode,
      dtMs: number
    ) => {
      const WIDTH = canvas.width
      const HEIGHT = canvas.height
      const PITCH = 12
      const RADIUS = 3.5
      const cols = Math.max(1, Math.floor(WIDTH / PITCH))
      const rows = Math.max(1, Math.floor(HEIGHT / PITCH))
      const offsetX = (WIDTH - cols * PITCH) / 2
      const offsetY = (HEIGHT - rows * PITCH) / 2

      const bufferLength = analyser.frequencyBinCount
      const dataArray = freqBuffer(bufferLength)
      readFreq(analyser, dataArray)
      const useful = Math.max(cols, Math.floor(bufferLength * 0.7))
      const binsPerCol = useful / cols

      let peaks = matrixPeakCache.get(canvas)
      if (!peaks || peaks.length !== cols) {
        peaks = new Float32Array(cols)
        matrixPeakCache.set(canvas, peaks)
      }

      const themeKey = currentThemeRef.current
      const themeColor = THEMES[themeKey].waveformColor
      const mono = monoColorRef.current
      const gain = MATRIX_SCALE * scaleCoefficientRef.current
      const decay = (dtMs / 1000) * 6
      const litRows = new Array<number>(cols)

      for (let col = 0; col < cols; col++) {
        const start = Math.floor(col * binsPerCol)
        const end = Math.min(
          useful,
          Math.max(start + 1, Math.floor((col + 1) * binsPerCol))
        )
        let sum = 0
        for (let i = start; i < end; i++) sum += dataArray[i]
        const avg = sum / (end - start)
        const lit = Math.max(0, Math.min(rows, (avg / 255) * gain * rows))
        litRows[col] = Math.round(lit)
        if (lit >= peaks[col]) peaks[col] = lit
        else peaks[col] = Math.max(lit, peaks[col] - decay)
      }

      const addDot = (col: number, row: number) => {
        const cx = offsetX + col * PITCH + PITCH / 2
        const cy = offsetY + (rows - 1 - row) * PITCH + PITCH / 2
        ctx.moveTo(cx + RADIUS, cy)
        ctx.arc(cx, cy, RADIUS, 0, Math.PI * 2)
      }

      ctx.shadowBlur = 0
      ctx.globalAlpha = 0.1
      ctx.fillStyle = themeColor
      ctx.beginPath()
      for (let col = 0; col < cols; col++) {
        for (let row = 0; row < rows; row++) addDot(col, row)
      }
      ctx.fill()

      ctx.globalAlpha = 1
      const glow = waveformGlowRef.current
      for (let col = 0; col < cols; col++) {
        const lit = litRows[col]
        const peakCount = Math.floor(peaks[col])
        if (lit <= 0 && peakCount <= 0) continue
        ctx.fillStyle = barFill(
          themeKey,
          col,
          cols,
          lit * 8,
          true,
          mono,
          themeColor
        )
        if (glow) {
          ctx.shadowBlur = 12
          ctx.shadowColor = themeColor
        }
        ctx.beginPath()
        for (let row = 0; row < lit; row++) addDot(col, row)
        if (peakCount > lit) addDot(col, peakCount - 1)
        ctx.fill()
      }

      ctx.globalAlpha = 1
      ctx.shadowBlur = 0
    }

    function cleanupAudio() {
      if (cleaned) return
      cleaned = true
      isActive = false
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId)
      }
      // Meters share this source. Disconnecting it would silence them.
      if (splitterRef.current) {
        splitterRef.current.disconnect()
      }
      if (analyserLRef.current) {
        analyserLRef.current.disconnect()
      }
      if (analyserRRef.current) {
        analyserRRef.current.disconnect()
      }
      if (
        analyserMonoRef.current &&
        analyserMonoRef.current !== analyserLRef.current
      ) {
        analyserMonoRef.current.disconnect()
      }
      const published = retainedInput
      retainedInput = null
      if (vizSessionRef.current?.teardown === cleanupAudio) {
        vizSessionRef.current = null
      }
      if (published) {
        published.release()
        primedContextRef.current = null
      } else if (
        releaseUnpublishedPrime &&
        primedContextRef.current &&
        primedContextRef.current.state !== 'closed'
      ) {
        // Listening was cancelled before the graph was shared.
        releaseAudioContextResume(primedContextRef.current)
        primedContextRef.current.close().catch(() => {})
        primedContextRef.current = null
      }
      audioContextRef.current = null
      setSharedInput(null)
      analyserRef.current = null
      sourceRef.current = null
      splitterRef.current = null
      analyserLRef.current = null
      analyserRRef.current = null
      analyserMonoRef.current = null

      shockwaveRingsLRef.current = []
      shockwaveRingsRRef.current = []
      lastBeatTimeLRef.current = 0
      lastBeatTimeRRef.current = 0
    }

    if (isListening) {
      vizSessionRef.current?.teardown()
      setupAudio()
      return () => {
        if (!isListeningRef.current && deckPoweredRef.current) {
          if (vizSessionRef.current?.teardown === cleanupAudio) {
            vizSessionRef.current.mute()
            return
          }
        }
        cleanupAudio()
      }
    }

    return
  }, [
    isListening,
    selectedDeviceId,
    isStereo,
    getAudioDevices,
  ])

  useEffect(() => {
    return () => {
      vizSessionRef.current?.teardown()
    }
  }, [])

  // Recording
  const [recordFormat, setRecordFormat] = useState<RecordFormat>(() => {
    const saved = localStorage.getItem('recordFormat')
    return saved === 'webm' || saved === 'wav' || saved === 'mp3'
      ? saved
      : 'mp3'
  })

  useEffect(() => {
    localStorage.setItem('recordFormat', recordFormat)
  }, [recordFormat])

  const { isRecording, startRecording, stopRecording } = useAudioRecorder(
    selectedDeviceId,
    recordFormat
  )
  const [uiSoundsOn, setUiSoundsOn] = useState(() => {
    const saved = localStorage.getItem('uiSoundsOn')
    return saved !== null ? saved === 'true' : true
  })

  useEffect(() => {
    localStorage.setItem('uiSoundsOn', String(uiSoundsOn))
  }, [uiSoundsOn])

  // Created inside the click so Safari, including a Dock web app, will
  // leave the context running instead of suspended.
  const primeAudioContext = () => {
    const Context =
      window.AudioContext || (window as any).webkitAudioContext
    if (!Context) return
    const existing = primedContextRef.current
    if (!existing || existing.state === 'closed') {
      primedContextRef.current = new Context()
    }
    const context = primedContextRef.current
    if (context && context.state === 'suspended') {
      context.resume().catch(() => {})
    }
  }

  const toggleListening = () => {
    if (!deckPowered) return
    if (!isListening) primeAudioContext()
    setIsListening((listening) => !listening)
  }

  const toggleRecording = () => {
    if (!deckPowered) return
    if (isRecording) {
      stopRecording()
    } else {
      primeAudioContext()
      setIsListening(true)
      startRecording()
    }
  }

  const toggleDeckPower = () => {
    if (deckPowered) {
      setIsListening(false)
      stopRecording()
      setDeckPowered(false)
      return
    }
    setDeckPowered(true)
  }

  // Panel drag handlers
  const handlePanelMouseDown = (e: React.MouseEvent) => {
    if (panelRef.current) {
      setIsDragging(true)
      const rect = panelRef.current.getBoundingClientRect()
      setDragStart({
        x: e.clientX - rect.left - panelPosition.x,
        y: e.clientY - rect.top - panelPosition.y,
      })
    }
  }

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const newX = e.clientX - dragStart.x
        const newY = e.clientY - dragStart.y

        // Keep panel within viewport
        const maxX = window.innerWidth - (panelRef.current?.offsetWidth || 600)
        const maxY =
          window.innerHeight - (panelRef.current?.offsetHeight || 400)

        setPanelPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
    }

    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove)
      window.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, dragStart])

  // Drawing handlers
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawingMode || !containerRef.current) return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setDrawingRect({
      startX: x,
      startY: y,
      endX: x,
      endY: y,
      isDrawing: true,
    })
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawingMode || !drawingRect?.isDrawing || !containerRef.current)
      return

    const rect = containerRef.current.getBoundingClientRect()
    const x = e.clientX - rect.left
    const y = e.clientY - rect.top

    setDrawingRect({
      ...drawingRect,
      endX: x,
      endY: y,
    })
  }

  const handleMouseUp = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDrawingMode || !drawingRect?.isDrawing || !containerRef.current)
      return

    const rect = containerRef.current.getBoundingClientRect()
    const endX = e.clientX - rect.left
    const endY = e.clientY - rect.top

    const minX = Math.min(drawingRect.startX, endX)
    const minY = Math.min(drawingRect.startY, endY)
    const width = Math.abs(endX - drawingRect.startX)
    const height = Math.abs(endY - drawingRect.startY)

    // Convert to percentages
    const containerWidth = rect.width
    const containerHeight = rect.height

    const percentX = (minX / containerWidth) * 100
    const percentY = (minY / containerHeight) * 100
    const percentWidth = (width / containerWidth) * 100
    const percentHeight = (height / containerHeight) * 100

    // Apply to selected custom element (windows/VCR are now driven by CSS)
    const customElement = customElements.find((el) => el.id === targetElement)
    if (customElement) {
      setCustomElements(
        customElements.map((el) =>
          el.id === targetElement
            ? {
                ...el,
                top: percentY,
                left: percentX,
                width: percentWidth,
                height: percentHeight,
              }
            : el
        )
      )
    }

    // Store rectangle for visual reference
    setDrawnRectangles([
      ...drawnRectangles,
      {
        x: percentX,
        y: percentY,
        width: percentWidth,
        height: percentHeight,
        element: String(targetElement),
      },
    ])

    setDrawingRect(null)
  }

  // Clear rectangles when exiting drawing mode or calibration
  useEffect(() => {
    if (!isDrawingMode) {
      setDrawingRect(null)
    }
  }, [isDrawingMode])

  useEffect(() => {
    if (!isCalibrating) {
      setDrawnRectangles([])
      setIsDrawingMode(false)
    }
  }, [isCalibrating])

  // Reset all calibration values
  const resetCalibration = () => {
    setCustomElements([...DEFAULT_CUSTOM_ELEMENTS])
    setNextCustomId(2)
    setTargetElement('custom-1')
    setDrawnRectangles([])
  }

  // Add new custom element
  const addCustomElement = () => {
    const newId = `custom-${nextCustomId}`
    const newElement = {
      id: newId,
      name: `Custom Element ${nextCustomId}`,
      top: 50,
      left: 50,
      width: 20,
      height: 20,
    }
    setCustomElements([...customElements, newElement])
    setNextCustomId(nextCustomId + 1)
    setTargetElement(newId)
  }

  // Delete custom element
  const deleteCustomElement = (id: string) => {
    const remaining = customElements.filter((el) => el.id !== id)
    setCustomElements(remaining)
    if (targetElement === id) {
      setTargetElement(remaining[0]?.id ?? '')
    }
  }

  // Update custom element name
  const updateCustomElementName = (id: string, name: string) => {
    setCustomElements(
      customElements.map((el) => (el.id === id ? { ...el, name } : el))
    )
  }

  // Copy CSS to clipboard
  const copyCSSToClipboard = async () => {
    let cssCode = '/* Custom Elements */\n'
    customElements.forEach((el) => {
      cssCode += `/* ${el.name} */\n`
      if (el.top !== undefined) cssCode += `top: ${el.top}%;\n`
      if (el.left !== undefined) cssCode += `left: ${el.left}%;\n`
      if (el.right !== undefined) cssCode += `right: ${el.right}%;\n`
      if (el.bottom !== undefined) cssCode += `bottom: ${el.bottom}%;\n`
      if (el.width !== undefined) cssCode += `width: ${el.width}%;\n`
      if (el.height !== undefined) cssCode += `height: ${el.height}%;\n`
      cssCode += '\n'
    })

    try {
      await navigator.clipboard.writeText(cssCode)
      setCopySuccess(true)
      setTimeout(() => setCopySuccess(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
      // Fallback for older browsers
      const textArea = document.createElement('textarea')
      textArea.value = cssCode
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      try {
        document.execCommand('copy')
        setCopySuccess(true)
        setTimeout(() => setCopySuccess(false), 2000)
      } catch (fallbackErr) {
        console.error('Fallback copy failed:', fallbackErr)
      }
      document.body.removeChild(textArea)
    }
  }

  return (
    <SharedInputContext.Provider value={sharedInput}>
    <div
      className={`hifi-container ${deckPowered ? '' : 'powered-off'} ${displayMode}${monoColor ? ' one-color' : ''}`}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      style={
        {
          cursor: isDrawingMode ? 'crosshair' : 'default',
          '--theme-color': theme.waveformColor,
          '--theme-rgb': themeRgb,
          '--top-color': shadeBackground(
            theme.waveformGradient.top,
            backgroundLevelRef.current
          ),
          '--bottom-color': shadeBackground(
            theme.waveformGradient.bottom,
            backgroundLevelRef.current
          ),
        } as React.CSSProperties
      }
    >
      {/* Left VU Meter positioned for left dial */}
      {visualizationType === 'analog' && (
        <div
          className="hifi-vumeter hifi-vumeter-left"
          style={
            {
              display: isCalibrating ? 'none' : undefined,
              '--band-color': deckPowered ? theme.bandColor : '0, 0, 0',
            } as React.CSSProperties
          }
        >
          <VUMeter
            isListening={isListening && deckPowered}
            deviceId={selectedDeviceId}
            movementCoef={ANALOG_SCALE_LEFT}
            backgroundImage={deckPowered ? theme.deckImage : deckOff}
            channel="left"
          />
        </div>
      )}

      {/* Right VU Meter positioned for right dial */}
      {visualizationType === 'analog' && (
        <div
          className="hifi-vumeter hifi-vumeter-right"
          style={
            {
              display: isCalibrating ? 'none' : undefined,
              '--band-color': deckPowered ? theme.bandColor : '0, 0, 0',
            } as React.CSSProperties
          }
        >
          <VUMeter
            isListening={isListening && deckPowered}
            deviceId={selectedDeviceId}
            movementCoef={ANALOG_SCALE_RIGHT}
            backgroundImage={deckPowered ? theme.deckImage : deckOff}
            channel="right"
          />
        </div>
      )}

      {/* Left visualizer canvas. Mono spans both amplifier windows. */}
      <canvas
        ref={leftCanvasRef}
        className={`hifi-visualizer ${
          displayMode === 'mono' && visualizationType !== 'analog'
            ? 'hifi-visualizer-wide'
            : 'hifi-visualizer-left'
        } ${visualizationType !== 'analog' ? 'themed-bg' : ''}`}
        width={
          displayMode === 'mono' && visualizationType !== 'analog' ? 1020 : 400
        }
        height={200}
        style={{
          display:
            isCalibrating || visualizationType === 'analog' ? 'none' : 'block',
        }}
      />

      {/* Right visualizer canvas */}
      <canvas
        ref={rightCanvasRef}
        className={`hifi-visualizer hifi-visualizer-right ${visualizationType !== 'analog' ? 'themed-bg' : ''}`}
        width={400}
        height={200}
        style={{
          display:
            isCalibrating ||
            visualizationType === 'analog' ||
            displayMode === 'mono'
              ? 'none'
              : 'block',
        }}
      />

      {/* Peak Level Meter */}
      <div className="hifi-peak-meter-container">
        <PeakLevelMeter
          isListening={isListening && deckPowered}
          selectedDeviceId={selectedDeviceId}
          isStereo={isStereo}
        />
      </div>

      {/* Deck Background Skin */}
      <img className="hifi-deck-overlay" src={deckOverlay} alt="Hi-Fi Deck" />
      <a
        className="hifi-brand-link deck"
        href="https://generativeworks.net/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Generative Works"
      />
      <a
        className="hifi-brand-link amp"
        href="https://generativeworks.net/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Generative Works"
      />
      <button
        type="button"
        className={`hifi-response-toggle ${phosphorEnabled ? 'on' : ''}`}
        aria-pressed={phosphorEnabled}
        aria-label={phosphorEnabled ? 'Phosphor on' : 'Phosphor off'}
        title={phosphorEnabled ? 'Phosphor on' : 'Phosphor off'}
        onClick={() => setPhosphorEnabled((on) => !on)}
      >
        <span className="hifi-response-toggle-label">PHOSPHOR</span>
        <span className="hifi-response-toggle-track" aria-hidden="true">
          <span className="hifi-response-toggle-thumb" />
        </span>
      </button>
      <span className="hifi-painted-speaker-led left" aria-hidden="true" />
      <span className="hifi-painted-speaker-led right" aria-hidden="true" />

      <div
        className="hifi-amp-keys"
        role="group"
        aria-label="Visualizer layout"
      >
        <button
          type="button"
          className={`hifi-amp-key speakers ${displayMode === 'stereo' ? 'on' : ''}`}
          aria-pressed={displayMode === 'stereo'}
          onClick={() => setDisplayMode('stereo')}
        >
          Stereo
        </button>
        <button
          type="button"
          className={`hifi-amp-key mute ${displayMode === 'mono' ? 'on' : ''}`}
          aria-pressed={displayMode === 'mono'}
          onClick={() => {
            setDisplayMode('mono')
            if (visualizationType !== 'analog') return
            setActiveButton('bars')
            setShowPeaks(true)
            setVisualizationType('thickBars')
            setFxMode(null)
          }}
        >
          Mono
        </button>
      </div>

      {/* Tape Wheels */}
      <div
        className={`vc-tape-wheel vc-tape-wheel-left ${isListening && deckPowered ? 'playing' : ''}`}
      >
        <div
          style={
            { '--wheel-start': `${leftWheelStart}deg` } as React.CSSProperties
          }
        />
      </div>
      <div
        className={`vc-tape-wheel vc-tape-wheel-right ${isListening && deckPowered ? 'playing' : ''}`}
      >
        <div
          style={
            { '--wheel-start': `${rightWheelStart}deg` } as React.CSSProperties
          }
        />
      </div>

      {/* Color Cycle Buttons */}
      <ColorCycleButton
        direction="prev"
        icon="solid"
        active={monoColor}
        onClick={() => setMonoColor((on) => !on)}
      />
      <ColorCycleButton direction="next" onClick={cycleThemeNext} />

      {/* VCR Button */}
      <VCRButton
        onClick={toggleListening}
        isPlaying={isListening && deckPowered}
        soundEnabled={uiSoundsOn && deckPowered}
      />

      {/* LED Time Counter */}
      <div className="hifi-counter-container">
        <div className="hifi-operation-button-container">
          <LargeFlatButton
            labelTop=""
            ledColor="amber"
            onClick={() => setUiSoundsOn((on) => !on)}
            isActive={uiSoundsOn}
          />
        </div>
        <div
          className="counter-format-selector"
          aria-label={`Output format: ${recordFormat.toUpperCase()}`}
        >
          {(['webm', 'wav', 'mp3'] as RecordFormat[]).map((format) => (
            <div
              key={format}
              className={`counter-format-row ${
                recordFormat === format ? 'active' : ''
              }`}
            >
              <span className="counter-format-branch" aria-hidden="true" />
              <span className="counter-format-label">
                {format.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
        <LedTimeCounter
          isRunning={isRecording && deckPowered}
          isRecording={isRecording && deckPowered}
        />
      </div>

      {/* Input Device Selector */}
      <div className="hifi-device-selector">
        <InputDeviceDropdown
          selectedDeviceId={selectedDeviceId}
          audioDevices={audioDevices}
          disabled={isListening || !deckPowered}
          onChange={setSelectedDeviceId}
        />
      </div>

      {/* New Large Button Container (Left Side) */}
      <div className="hifi-large-button-container">
        <LargeFlatButton
          labelTop="GLOW"
          ledColor="amber"
          onClick={() => setWaveformGlow(!waveformGlow)}
          isActive={waveformGlow}
        />
        <LargeFlatButton
          labelTop="FAST"
          ledColor="amber"
          onClick={() => setFastResponse((on) => !on)}
          isActive={fastResponse}
        />
      </div>

      {/* Deck power. LED sits above the key, not on its face. */}
      <div
        className="hifi-power-button-container"
        title={deckPowered ? 'Power off' : 'Power on'}
      >
        <LargeFlatButton
          labelTop=""
          ledColor="amber"
          onClick={toggleDeckPower}
          isActive={deckPowered}
        />
      </div>

      {/* Synchronized signal knob in the GLOW button's former position */}
      <div className="hifi-knob-container hifi-knob-container-upper">
        <RetroKnob
          label="LUMINOSITY"
          min={0}
          max={100}
          labelStep={20}
          suffix="%"
          resetValue={25}
          snapTo={null}
          labelUpright
          initialValue={backgroundLevelRef.current}
          onChange={(value) => {
            backgroundLevelRef.current = value
            localStorage.setItem('visualizerBgLevel', String(value))
            paintBackground(value)
          }}
        />
      </div>

      {/* Secondary Flat Buttons Container - control effects */}
      <div className="hifi-flat-buttons-container-secondary">
        <FlatButton
          labelTop="SPEC"
          ledColor="green"
          onClick={selectSpectrogram}
          isActive={fxMode === 'spectrogram'}
        />
        <FlatButton
          labelTop="DOTS"
          ledColor={
            fxMode === 'shockwaves' && visualizationType === 'shockwaves'
              ? 'lime'
              : 'green'
          }
          onClick={selectDots}
          isActive={fxMode === 'shockwaves'}
        />
        <FlatButton
          labelTop="LINE"
          ledColor={fxMode === 'lines' && showPeaks ? 'lime' : 'green'}
          onClick={selectLines}
          isActive={fxMode === 'lines'}
        />
      </div>

      {/* Record Button - covers the REC key painted on the deck */}
      <div
        className={`hifi-rec-button-container ${isRecording ? 'recording' : ''}`}
        title={isRecording ? 'Stop recording' : 'Record'}
      >
        <LargeFlatButton
          labelTop="REC"
          ledColor="red"
          onClick={toggleRecording}
          isActive={isRecording}
        />
      </div>

      {/* Record Format Buttons - replace the painted COUNTER RESET / AMCS keys */}
      <div className="hifi-format-buttons-container">
        {(['webm', 'wav', 'mp3'] as RecordFormat[]).map((format) => (
          <LargeFlatButton
            key={format}
            labelTop={format.toUpperCase()}
            ledColor="amber"
            onClick={() => setRecordFormat(format)}
            isActive={recordFormat === format}
            disabled={isRecording || !deckPowered}
          />
        ))}
      </div>

      {/* Retro Knob */}
      <div className="hifi-knob-container hifi-knob-container-lower">
        <RetroKnob
          min={-50}
          max={50}
          labelStep={25}
          resetValue={0}
          snapTo={0}
          initialValue={knobRawValue}
          onChange={(value) => {
            localStorage.setItem('knobSignalValue', String(value))
            localStorage.setItem('knobSignalScale', SIGNAL_BOOST_SCALE)
            scaleCoefficientRef.current = signalBoostToCoefficient(value)
          }}
        />
      </div>

      {/* Flat Buttons */}
      <div className="hifi-flat-buttons-container">
        <FlatButton
          labelTop="WAVES"
          ledColor="green"
          onClick={() => {
            // If already active, do nothing (always one button must be ON)
            if (activeButton === 'waves') return
            setActiveButton('waves')
            setVisualizationType('waveform')
            setFxMode(null) // Exit FX mode
          }}
          isActive={activeButton === 'waves'}
        />
        <FlatButton
          labelTop="BARS"
          ledColor={activeButton === 'bars' && showPeaks ? 'lime' : 'green'}
          onClick={() => {
            if (activeButton === 'bars') {
              setShowPeaks((on) => !on)
              setVisualizationType('thickBars')
            } else {
              setActiveButton('bars')
              setShowPeaks(true)
              setVisualizationType('thickBars')
            }
            setFxMode(null)
          }}
          isActive={activeButton === 'bars'}
        />
        <FlatButton
          labelTop="ANALOG"
          ledColor="amber"
          onClick={() => {
            // If already active, do nothing (always one button must be ON)
            if (activeButton === 'analog') return
            setActiveButton('analog')
            setVisualizationType('analog')
            setFxMode(null) // Exit FX mode
          }}
          isActive={activeButton === 'analog'}
        />
      </div>

      {/* Drawing overlay */}
      {isDrawingMode && (
        <>
          {/* Active drawing rectangle */}
          {drawingRect && (
            <div
              style={{
                position: 'absolute',
                left: `${Math.min(drawingRect.startX, drawingRect.endX)}px`,
                top: `${Math.min(drawingRect.startY, drawingRect.endY)}px`,
                width: `${Math.abs(drawingRect.endX - drawingRect.startX)}px`,
                height: `${Math.abs(drawingRect.endY - drawingRect.startY)}px`,
                border: '2px dashed #f39c12',
                backgroundColor: 'rgba(243, 156, 18, 0.2)',
                pointerEvents: 'none',
                zIndex: 9999,
              }}
            />
          )}
          {/* Completed rectangles */}
          {drawnRectangles.map((rect, idx) => {
            if (!containerRef.current) return null
            const containerWidth = containerRef.current.offsetWidth || 1
            const containerHeight = containerRef.current.offsetHeight || 1
            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: `${(rect.x / 100) * containerWidth}px`,
                  top: `${(rect.y / 100) * containerHeight}px`,
                  width: `${(rect.width / 100) * containerWidth}px`,
                  height: `${(rect.height / 100) * containerHeight}px`,
                  border: '2px solid #2ecc71',
                  backgroundColor: 'rgba(46, 204, 113, 0.15)',
                  pointerEvents: 'none',
                  zIndex: 9998,
                }}
              />
            )
          })}
        </>
      )}

      {/* Calibration Panel */}
      {/* UI hidden for now (logic kept) */}
      {false && (
        <div
          className="hifi-calibration-panel"
          ref={panelRef}
          style={{
            position: 'fixed',
            left: `${panelPosition.x}px`,
            top: `${panelPosition.y}px`,
            transform: 'none',
            cursor: isDragging ? 'grabbing' : 'default',
          }}
        >
          <div
            className="calibration-panel-header"
            onMouseDown={handlePanelMouseDown}
            style={{
              cursor: 'grab',
              userSelect: 'none',
            }}
          >
            <h3>Calibration Mode</h3>
          </div>

          <div className="calibration-section">
            <h4>Drawing Tool</h4>
            <button
              className={`hifi-btn ${isDrawingMode ? 'selected' : ''}`}
              onClick={() => setIsDrawingMode(!isDrawingMode)}
              style={{ marginBottom: '10px', width: '100%' }}
            >
              {isDrawingMode ? 'Disable Drawing Mode' : 'Enable Drawing Mode'}
            </button>
            <label>
              Target Element:
              <select
                className="hifi-device-select"
                value={targetElement}
                onChange={(e) => setTargetElement(e.target.value)}
                style={{ marginTop: '5px', width: '100%' }}
              >
                {customElements.map((el) => (
                  <option key={el.id} value={el.id}>
                    {el.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className="hifi-btn"
              onClick={addCustomElement}
              style={{
                marginTop: '10px',
                width: '100%',
                fontSize: '12px',
                padding: '8px',
                background: 'linear-gradient(180deg, #27ae60 0%, #1e8449 100%)',
                borderColor: '#2ecc71',
              }}
            >
              + Add Custom Element
            </button>
            {drawnRectangles.length > 0 && (
              <button
                className="hifi-btn"
                onClick={() => setDrawnRectangles([])}
                style={{
                  marginTop: '10px',
                  width: '100%',
                  fontSize: '12px',
                  padding: '8px',
                }}
              >
                Clear Rectangles
              </button>
            )}
            <button
              className="hifi-btn"
              onClick={resetCalibration}
              style={{
                marginTop: '10px',
                width: '100%',
                fontSize: '12px',
                padding: '8px',
                background: 'linear-gradient(180deg, #c0392b 0%, #8b0000 100%)',
                borderColor: '#e74c3c',
              }}
            >
              Reset All
            </button>
          </div>

          {customElements.length > 0 && (
            <div className="calibration-section">
              <h4>Custom Elements</h4>
              {customElements.map((el) => (
                <div
                  key={el.id}
                  style={{
                    marginBottom: '15px',
                    padding: '10px',
                    background: 'rgba(46, 204, 113, 0.1)',
                    borderRadius: '4px',
                    border: '1px solid #2ecc71',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '8px',
                    }}
                  >
                    <input
                      type="text"
                      value={el.name}
                      onChange={(e) =>
                        updateCustomElementName(el.id, e.target.value)
                      }
                      style={{
                        flex: 1,
                        padding: '4px 8px',
                        background: 'rgba(0, 0, 0, 0.5)',
                        border: '1px solid #444',
                        borderRadius: '4px',
                        color: 'white',
                        fontSize: '12px',
                        marginRight: '8px',
                      }}
                    />
                    <button
                      className="hifi-btn"
                      onClick={() => deleteCustomElement(el.id)}
                      style={{
                        padding: '4px 8px',
                        fontSize: '11px',
                        background:
                          'linear-gradient(180deg, #c0392b 0%, #8b0000 100%)',
                        borderColor: '#e74c3c',
                        minWidth: 'auto',
                      }}
                    >
                      Delete
                    </button>
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      gap: '8px',
                    }}
                  >
                    {el.top !== undefined && (
                      <label style={{ fontSize: '11px' }}>
                        Top: {el.top.toFixed(1)}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={el.top}
                          onChange={(e) =>
                            setCustomElements(
                              customElements.map((elem) =>
                                elem.id === el.id
                                  ? { ...elem, top: parseFloat(e.target.value) }
                                  : elem
                              )
                            )
                          }
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                      </label>
                    )}
                    {el.left !== undefined && (
                      <label style={{ fontSize: '11px' }}>
                        Left: {el.left.toFixed(1)}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={el.left}
                          onChange={(e) =>
                            setCustomElements(
                              customElements.map((elem) =>
                                elem.id === el.id
                                  ? {
                                      ...elem,
                                      left: parseFloat(e.target.value),
                                    }
                                  : elem
                              )
                            )
                          }
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                      </label>
                    )}
                    {el.width !== undefined && (
                      <label style={{ fontSize: '11px' }}>
                        Width: {el.width.toFixed(1)}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={el.width}
                          onChange={(e) =>
                            setCustomElements(
                              customElements.map((elem) =>
                                elem.id === el.id
                                  ? {
                                      ...elem,
                                      width: parseFloat(e.target.value),
                                    }
                                  : elem
                              )
                            )
                          }
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                      </label>
                    )}
                    {el.height !== undefined && (
                      <label style={{ fontSize: '11px' }}>
                        Height: {el.height.toFixed(1)}%
                        <input
                          type="range"
                          min="0"
                          max="100"
                          step="0.1"
                          value={el.height}
                          onChange={(e) =>
                            setCustomElements(
                              customElements.map((elem) =>
                                elem.id === el.id
                                  ? {
                                      ...elem,
                                      height: parseFloat(e.target.value),
                                    }
                                  : elem
                              )
                            )
                          }
                          style={{ width: '100%', marginTop: '4px' }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="calibration-code">
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '8px',
              }}
            >
              <h4 style={{ margin: 0 }}>Generated CSS</h4>
              <button
                className="hifi-btn"
                onClick={copyCSSToClipboard}
                style={{
                  padding: '6px 12px',
                  fontSize: '12px',
                  minWidth: 'auto',
                }}
              >
                {copySuccess ? '✓ Copied!' : 'Copy CSS'}
              </button>
            </div>
            <pre>
              {(() => {
                let code = '/* Custom Elements */\n'
                customElements.forEach((el) => {
                  code += `/* ${el.name} */\n`
                  if (el.top !== undefined) code += `top: ${el.top}%;\n`
                  if (el.left !== undefined) code += `left: ${el.left}%;\n`
                  if (el.right !== undefined) code += `right: ${el.right}%;\n`
                  if (el.bottom !== undefined)
                    code += `bottom: ${el.bottom}%;\n`
                  if (el.width !== undefined) code += `width: ${el.width}%;\n`
                  if (el.height !== undefined)
                    code += `height: ${el.height}%;\n`
                  code += '\n'
                })
                return code
              })()}
            </pre>
          </div>
        </div>
      )}
    </div>
    </SharedInputContext.Provider>
  )
}

export default HiFiVisualizer
